import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { dontShowMobileBannerKey } from '@/app/config/localStorageKeys'
import {
  MOBILE_APP_CONTENT,
  type MobileAppVariant,
} from '@/app/config/mobileAppContent'
import { configService } from '@/app/services/configService'
import { usersService } from '@/app/services/usersService'
import { useIsMobile } from '@/app/hooks/useIsMobile'
import { useToast } from '../ui/use-toast'

interface UseMobileAppModalControllerParams {
  variant: MobileAppVariant
  onOpenChange: (open: boolean) => void
}

export const useMobileAppModalController = ({
  variant,
  onOpenChange,
}: UseMobileAppModalControllerParams) => {
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const { toast } = useToast()

  const isMobile = useIsMobile()
  const isDesktop = !isMobile
  // Página pública que detecta o SO do celular e redireciona para a loja certa.
  const downloadPageUrl = `${window.location.origin}/baixar?type=${variant}`

  const { data: configsApp } = useQuery({
    queryKey: ['configs'],
    queryFn: configService.getConfigsApp,
  })

  const storeLinks =
    variant === 'admin' ? configsApp?.admin_app : configsApp?.generic_app

  const {
    mutate: generateCode,
    data,
    isPending: isGenerating,
    isError,
    reset,
  } = useMutation({
    mutationFn: usersService.userAccess,
  })

  const accessCode = data?.code ?? ''
  const hasCode = accessCode.length > 0
  const appleUrl = storeLinks?.apple ?? '#'
  const googleUrl = storeLinks?.google ?? '#'
  const learnMoreUrl = storeLinks?.learn_more_url?.trim() || undefined

  const handleGenerateCode = () => generateCode()

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (dontShowAgain) {
        localStorage.setItem(dontShowMobileBannerKey(variant), 'true')
      }
      reset()
    }
    onOpenChange(newOpen)
  }

  const handleCopyCode = () => {
    if (!accessCode) return
    navigator.clipboard.writeText(accessCode).then(() => {
      toast({
        title: 'Código copiado',
        variant: 'default',
      })
    })
  }

  const getAppStoreUrl = () => {
    const ua = navigator.userAgent.toLowerCase()
    if (ua.includes('iphone') || ua.includes('ipad')) {
      return appleUrl
    }
    return googleUrl
  }

  const appStoreUrl = getAppStoreUrl()
  const displayUrl =
    appStoreUrl.length > 36 ? `${appStoreUrl.substring(0, 33)}...` : appStoreUrl

  const accessCodeDigits = accessCode.split('')

  return {
    dontShowAgain,
    setDontShowAgain,
    handleOpenChange,
    handleCopyCode,
    handleGenerateCode,
    generateCodeCta: MOBILE_APP_CONTENT[variant].generateCodeCta,
    appStoreUrl,
    displayUrl,
    learnMoreUrl,
    accessCodeDigits,
    hasCode,
    isGenerating,
    isError,
    isDesktop,
    downloadPageUrl,
  }
}
